import { CBCT_SectionOrientation } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import { ToothFullHeadMPRSliceGeometrySource } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import type {
  FullHeadMPRInputMode,
  FullHeadMPRProjection,
  FullHeadMPRToothSliceOrientation,
  ToothBasisLocalAxisName,
  ToothBasisSeriesName,
  ToothParityDisplacementReference,
  ToothParityInPlaneReference,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import { MedicalImagesGroups } from '@/entities/assets';
import {
  DEFAULT_TOOTH_MPR_SERIES_OPTIONS,
  type ToothMPRSeriesKey,
} from '@/entities/reports';

import { type MPRInputMode, type MPRTool } from '@/features/MPR';

import type {
  ToothGeometrySource,
  ToothSliceOptions,
  ToothSliceOptionsBySeries,
} from './types';

export {
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  ORIENTATION_TO_SERIES,
  SERIES_TO_ORIENTATION,
} from '@/features/MPR';

export const TOOTH_SLICE_LOOKAHEAD_PX = 200;
export const FULL_HEAD_MPR_SAVED_VIEW_PREVIEW_SIDE_PX = 512;
export const FULL_HEAD_MPR_SAVED_VIEW_ZOOM_SIDE_PX = 2048;
export const FULL_HEAD_MPR_SESSION_PROJECTIONS: FullHeadMPRProjection[] = [
  'axial',
  'coronal',
  'sagittal',
];

export const FULL_HEAD_MPR_SLICE_PLACEHOLDERS = [
  { key: 'axial-crown', aspectRatio: 1 },
  { key: 'axial-middle', aspectRatio: 1 },
  { key: 'axial-root', aspectRatio: 1 },
  { key: 'frontal-centroid', aspectRatio: 0.63 },
  { key: 'sagittal-mesial', aspectRatio: 0.46 },
  { key: 'sagittal-distal', aspectRatio: 0.46 },
];

export const FULL_HEAD_MPR_ERROR_CODES = {
  dataStream: 'data-stream',
  missingVolume: 'missing-volume',
  startViewer: 'start-viewer',
} as const;

export const FULL_HEAD_MPR_INPUT_MODE_BY_MODE: Record<
  MPRInputMode,
  FullHeadMPRInputMode
> = {
  mainMode: 'navigate',
  contrastBrightnessMode: 'brightnessContrast',
  panMode: 'pan',
  zoomMode: 'zoom',
  scrollThroughVolumeMode: 'scroll',
  rotateMode: 'rotate',
};

export const FULL_HEAD_MPR_TOOLS: MPRTool[] = [
  'mainMode',
  'contrastBrightnessMode',
  'panMode',
  'zoomMode',
  'scrollThroughVolumeMode',
  'rotateMode',
  'reset',
];

export const TOOTH_BASIS_SERIES_ORDER: ToothBasisSeriesName[] = [
  'axial',
  'sagittal',
  'frontal',
];

export const TOOTH_BASIS_SERIES_TO_GROUP: Record<
  ToothBasisSeriesName,
  MedicalImagesGroups
> = {
  axial: MedicalImagesGroups.OrientationAxial,
  sagittal: MedicalImagesGroups.OrientationVestibuloOral,
  frontal: MedicalImagesGroups.OrientationMesioDistal,
};

export const SERIES_TO_OPTIONS_KEY: Record<
  ToothBasisSeriesName,
  ToothMPRSeriesKey
> = {
  axial: 'Axial',
  sagittal: 'VestibuloOral',
  frontal: 'MesioDistal',
};

export const ORIENTATION_GROUP_TYPES: MedicalImagesGroups[] = [
  MedicalImagesGroups.OrientationAxial,
  MedicalImagesGroups.OrientationVestibuloOral,
  MedicalImagesGroups.OrientationMesioDistal,
];

export const CLIENT_SLICE_GUIDE_SOURCES: Record<
  ToothBasisSeriesName,
  ToothBasisSeriesName[]
> = {
  axial: ['sagittal', 'frontal'],
  sagittal: ['frontal', 'axial'],
  frontal: ['sagittal', 'axial'],
};

export const SERIES_OFFSET_AXIS: Record<
  ToothBasisSeriesName,
  ToothBasisLocalAxisName
> = {
  axial: 'cr',
  sagittal: 'md',
  frontal: 'vo',
};

export const GEOMETRY_SOURCE_TO_PROTO: Record<
  ToothGeometrySource | 'view',
  ToothFullHeadMPRSliceGeometrySource
> = {
  protocol:
    ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceProtocol,
  approximated:
    ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceApproximated,
  view: ToothFullHeadMPRSliceGeometrySource.ToothFullHeadMPRSliceGeometrySourceView,
};

export const GUIDE_AXIS_EPSILON = 1e-6;

export const PROBE_PROFILE_SAMPLES = 64;

export const MINIMUM_POSITION_STEP_MM = 1e-6;
export const STRIDE_EQUALITY_TOLERANCE_MM = 1e-6;
export const MIN_WINDOW_WIDTH_HU = 1;

export const ENGINE_ORIENTATION_BY_SECTION: Partial<
  Record<CBCT_SectionOrientation, FullHeadMPRToothSliceOrientation>
> = {
  [CBCT_SectionOrientation.SectionOrientationAxial]: 'axial',
  [CBCT_SectionOrientation.SectionOrientationVestibuloOral]: 'vestibuloOral',
  [CBCT_SectionOrientation.SectionOrientationMesioDistal]: 'mesioDistal',
};

export const PARITY_PIXEL_TRANSFORMS = [
  'identity',
  'flip-u',
  'flip-v',
  'rot180',
] as const;

export const SWEEP_REFERENCES: ToothParityDisplacementReference[] = [
  'outer-box-min',
  'outer-box-max',
  'origin',
];
export const SWEEP_SIGNS: (1 | -1)[] = [1, -1];
export const SWEEP_IN_PLANE_REFERENCES: ToothParityInPlaneReference[] = [
  'outer-box-center',
  'origin',
];
export const SWEEP_AXIS_SWAPS = [false, true];
export const SWEEP_PLANE_SPACES: ('tooth' | 'volume')[] = ['volume', 'tooth'];

export const DEFAULT_CARD_AXIAL_CROWN_INSET_MM = 3;
export const DEFAULT_CARD_AXIAL_ROOT_INSET_MM = 4;
export const DEFAULT_CARD_AXIAL_SHORT_CROWN_RATIO = 0.2;
export const DEFAULT_CARD_AXIAL_SHORT_ROOT_RATIO = 0.8;

export const TOOTH_TISSUE_MASS_FLOOR_RATIO = 0.05;
export const TOOTH_CROWN_LENGTH_RATIO = 0.4;
export const TOOTH_AXIAL_LEVEL_MIN_TISSUE_BINS = 8;

export const TOOTH_ROOT_ISLAND_FLOOR_RATIO = 0.2;
export const TOOTH_ROOT_MIN_ISLAND_BINS = 3;
export const TOOTH_ROOT_ROWS_FALLBACK_RATIO = 0.5;
export const TOOTH_AXIAL_ROOT_LEVEL_RATIO = 0.95;
export const TOOTH_ROOT_DEPTH_SCAN_RANGE: [number, number] = [0.35, 0.95];
export const TOOTH_ROOT_DEPTH_WINDOW_ROWS = 3;

export const TOOTH_AXIAL_CORONAL_ROOT_LEVEL_RATIO = 1 / 3;

export const DEFAULT_CARD_CROSS_SECTION_PAIR_SPREAD_MM = 1;

export const TOOTH_QUADRANTS = [1, 2, 3, 4] as const;
export const PRIMARY_QUADRANT_OFFSET = 4;

export const MAX_TOOTH_BASIS_SERIES_ABORTS = 3;

export const TOOTH_SERIES_ABANDON_GRACE_MS = 0;

export const TOOTH_BASIS_SLICE_ZOOM_CACHE_LIMIT = 6;

export const TOOTH_BASIS_SERIES_CACHE_LIMIT = 8;

export const TOOTH_SLICE_STEP_OPTIONS_MM = [0.25, 0.5, 1, 1.5, 2, 3];

export const TOOTH_SLICE_THICKNESS_OPTIONS_MM = [0.25, 0.5, 1, 2];

export const DEFAULT_TOOTH_SLICE_OPTIONS: ToothSliceOptions = {
  sliceStepMm: DEFAULT_TOOTH_MPR_SERIES_OPTIONS.SliceStepMm,
  thicknessMm: DEFAULT_TOOTH_MPR_SERIES_OPTIONS.ThicknessMm,
};

export const DEFAULT_TOOTH_SLICE_OPTIONS_BY_SERIES: ToothSliceOptionsBySeries =
  {
    axial: DEFAULT_TOOTH_SLICE_OPTIONS,
    sagittal: DEFAULT_TOOTH_SLICE_OPTIONS,
    frontal: DEFAULT_TOOTH_SLICE_OPTIONS,
  };

export const VALID_FDI_ISO_NUMBERS = [
  11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25, 26, 27, 28, 31, 32, 33,
  34, 35, 36, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 51, 52, 53, 54, 55, 61,
  62, 63, 64, 65, 71, 72, 73, 74, 75, 81, 82, 83, 84, 85,
] as const;
